function getPlatformConfig() {
	return window.gmrPlatform || {};
}

function normalizeDomain(domain) {
	return String(domain || '')
		.trim()
		.toLowerCase()
		.replace(/^https?:\/\//, '')
		.replace(/\/.*$/, '')
		.replace(/:\d+$/, '');
}

function getNormalizedDomains(domains) {
	if (!Array.isArray(domains)) {
		return [];
	}

	// The PHP config is intentionally simple for developers to maintain. The
	// browser normalizes values here so future entries can be bare domains or
	// copied URLs without changing the matching behavior.
	return domains.map(normalizeDomain).filter(Boolean);
}

function hostMatchesDomain(hostname, domain) {
	const normalizedHost = normalizeDomain(hostname);
	const normalizedDomain = normalizeDomain(domain);

	if (!normalizedHost || !normalizedDomain) {
		return false;
	}

	// Match the exact host or a true subdomain boundary. The leading dot check
	// prevents `badexample.com` from matching a configured `example.com` entry.
	return normalizedHost === normalizedDomain || normalizedHost.endsWith(`.${normalizedDomain}`);
}

function hostMatchesAnyDomain(hostname, domains) {
	return getNormalizedDomains(domains).some((domain) =>
		hostMatchesDomain(hostname, domain)
	);
}

function isExternalUrl(url) {
	const currentHost = window.location.hostname;

	if (url.origin === window.location.origin) {
		return false;
	}

	if (url.hostname === currentHost) {
		return false;
	}

	return /^https?:$/.test(url.protocol);
}

function shouldSkipLink(link, url) {
	const href = link.getAttribute('href') || '';
	const whitelistedDomains = getPlatformConfig().externalModalWhitelistedDomains || [];

	if (!href || href.startsWith('#')) {
		return true;
	}

	if (link.closest('.modal')) {
		return true;
	}

	if (link.hasAttribute('download')) {
		return true;
	}

	if (
		link.hasAttribute('data-gmr-modal-optout') ||
		link.hasAttribute('data-gmr-skip-external-modal') ||
		link.hasAttribute('data-no-external-modal')
	) {
		return true;
	}

	if (
		href.startsWith('mailto:') ||
		href.startsWith('tel:') ||
		href.startsWith('javascript:')
	) {
		return true;
	}

	if (link.dataset.bsToggle || link.getAttribute('role') === 'button') {
		return true;
	}

	if (url.pathname.includes('/wp-admin') || url.pathname.includes('/wp-login.php')) {
		return true;
	}

	if (!isExternalUrl(url)) {
		return true;
	}

	return hostMatchesAnyDomain(url.hostname, whitelistedDomains);
}

function getExternalModalMessage(url) {
	const config = getPlatformConfig();
	const messages = config.externalModalMessages || {};
	const lifecentsDomains = config.externalModalLifeCentsDomains || [];

	// LifeCents links need relationship-specific legal copy. Every other
	// eligible external destination receives the generic Wells Fargo warning.
	if (hostMatchesAnyDomain(url.hostname, lifecentsDomains)) {
		return messages.lifecents || messages.default || {};
	}

	return messages.default || messages.lifecents || {};
}

export function initExternalLinks() {
	const modalId = getPlatformConfig().externalModalId;
	const modalRoot = modalId ? document.getElementById(modalId) : null;
	const continueLink = modalRoot ? modalRoot.querySelector('[data-gmr-external-continue]') : null;
	const title = modalRoot ? modalRoot.querySelector('[data-gmr-external-title]') : null;
	const message = modalRoot ? modalRoot.querySelector('[data-gmr-external-message]') : null;
	const urlLabel = modalRoot ? modalRoot.querySelector('[data-gmr-external-url]') : null;
	const ModalClass = window.bootstrap && window.bootstrap.Modal;

	if (!modalRoot || !continueLink || !title || !message || !urlLabel || !ModalClass) {
		return;
	}

	let modal = new ModalClass(modalRoot);
	let lastTrigger = null;

	modalRoot.addEventListener('hidden.bs.modal', function () {
		// Clean up Bootstrap's modal state
		document.body.classList.remove('modal-open');
		document.querySelectorAll('.modal-backdrop').forEach((backdrop) => backdrop.remove());
		modalRoot.removeAttribute('inert');

		if (lastTrigger && typeof lastTrigger.focus === 'function') {
			lastTrigger.focus();

			// Resume carousel autoplay that was interrupted by the modal.
			const carouselRoot = lastTrigger.closest('.splide');

			if (carouselRoot && carouselRoot.splide) {
				const autoplay = carouselRoot.splide.Components?.Autoplay;

				if (autoplay && typeof autoplay.play === 'function') {
					autoplay.play();
				}
			}
		}
	});

	document.addEventListener('click', function (event) {
		const link = event.target.closest('a[href]');

		if (!link || event.defaultPrevented) {
			return;
		}

		let url;

		try {
			url = new URL(link.href, window.location.origin);
		} catch {
			return;
		}

		if (shouldSkipLink(link, url)) {
			return;
		}

		event.preventDefault();

		lastTrigger = link;
		const modalMessage = getExternalModalMessage(url);

		if (modalMessage.title) {
			title.textContent = modalMessage.title;
		}

		if (modalMessage.body) {
			message.textContent = modalMessage.body;
		}

		continueLink.setAttribute('href', url.toString());
		continueLink.setAttribute('target', link.getAttribute('target') || '_self');
		continueLink.setAttribute('rel', link.getAttribute('target') === '_blank' ? 'noopener noreferrer' : '');
		urlLabel.textContent = url.hostname;

		modal.show();
	});
}
